import React, { useEffect, useState, useMemo } from "react";
import { bestaandeRapporten } from "./bestaande-rapporten-api";
import { useRequestInit, Icon, formatDateTime } from "adviesbox-shared";
import "./bestaande-rapporten.scss";
import { Table } from "react-bootstrap";
import { CardLoadingSpinner } from "../../../dashboard/new/scenarios/card-loading-spinner";
import { RapportOutput } from "../../../.generated/rapportage/rapportagetypes";
import { ZonedDateTime } from "@js-joda/core";

interface RapportenOverzichtProps {
  isOpen: boolean;
  downloading: string | null;
  handleDownload: (rapport: RapportOutput) => Promise<void>
}

const BestaandeRapporten: React.FC<RapportenOverzichtProps> = ({ isOpen, downloading, handleDownload }): JSX.Element => {
  const [rapporten, setRapporten] = useState<RapportOutput[] | null | undefined>();
  const { settings, user, params } = useRequestInit();

  // Clear rapporten when dropdown is closed
  useEffect(() => {
    if (!isOpen) {
      setRapporten(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    const controller = new AbortController();

    const getRapporten = async (): Promise<void> => {
      try {
        const rapporten = await bestaandeRapporten(
          settings,
          user,
          params.vestiging || /* istanbul ignore next */ "",
          params.adviesdossier || /* istanbul ignore next */ "",
          controller.signal
        );
        /* istanbul ignore else */
        if (!controller.signal.aborted) {
          setRapporten(rapporten);
        }
      } catch (error) {
        /* istanbul ignore next */
        if (!controller.signal.aborted) {
          setRapporten(null);
        }
      }
    };

    if (isOpen) {
      /* eslint-disable-next-line @typescript-eslint/no-floating-promises */
      getRapporten();
    }

    return () => {
      controller.abort();
    };
  }, [settings, user, params, isOpen]);

  /* istanbul ignore next */
  const formatDate = (date: string | null): string => {
    if (!date) return "";
    return formatDateTime(ZonedDateTime.parse(date));
  };

  // Sort rapporten by aanmaakdatum in descending order (newest first)
  const sortedRapporten = useMemo(() => {
    if (!rapporten) return rapporten;
    return [...rapporten].sort((a, b) => {
      if (!a.aanmaakdatum) return 1;
      if (!b.aanmaakdatum) return -1;
      return new Date(b.aanmaakdatum).getTime() - new Date(a.aanmaakdatum).getTime();
    });
  }, [rapporten]);

  return (
    <div className="bestaande-rapporten dropdown__popup">
      <div className="dropdown__title mr-auto">
        <span>Documenten</span>
        <span>({rapporten?.length ?? 0})</span>
      </div>
      {(!rapporten || rapporten?.length === 0) && (
        <div className="no-data delayed-show">Er zijn geen rapporten gevonden</div>
      )}
      <div className="dropdown__body">
        {rapporten === undefined && (
          <div className="mx-auto">
            <CardLoadingSpinner />
          </div>
        )}

        {rapporten && rapporten.length > 0 && (
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Soort</th>
                <th>Bestandsnaam</th>
                <th>Aanmaakdatum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedRapporten?.map(rapport => (
                <tr key={rapport.rapportId}>
                  <td>{rapport.type}</td>
                  <td>{rapport.omschrijving}</td>
                  <td>{formatDate(rapport.aanmaakdatum).toString()}</td>
                  <td>
                  
                    <button
                      className="no-btn p-1 btn-link"
                      onClick={() => handleDownload(rapport)}
                      disabled={downloading === rapport.rapportId}
                    >
                      <Icon name={downloading === rapport.rapportId ? "loading" : "download"} />
                    </button>
                   {(window.location.hostname === "localhost" || window.location.hostname === "test.advies.adviesbox.nl") && <a
                      href={`http://localhost:3010/vestiging/${params.vestiging || /* istanbul ignore next */ ""}/adviesdossier/${params.adviesdossier || /* istanbul ignore next */ ""}/rapport/${rapport.rapportId}?bearer=${user?.access_token || /* istanbul ignore next */ ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="no-btn p-1 btn-link ml-1"
                      title="Open rapport in rapport app"
                    >
                      <Icon name="documentpreview" />
                    </a>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default BestaandeRapporten;
